import { isString } from "../types/isString";

/**
 * Checks if the given value is a valid phone number.
 *
 * This function provides a high-level validation that is not too strict.
 * It accepts various phone number formats including:
 * - International format with + prefix (e.g., +31612345678, +1-555-123-4567)
 * - Numbers with country code without + (e.g., 31612345678, 0031612345678)
 * - Local Dutch numbers (e.g., 0612345678, 020-1234567)
 * - Numbers with various separators (spaces, dashes, dots, parentheses)
 *
 * The validation ensures:
 * - At least 7 digits (to cover local numbers)
 * - Maximum 15 digits (ITU-T E.164 standard)
 * - Optional country code with + or 00 prefix
 * - Allows common formatting characters
 *
 * @param value - The value to be checked.
 * @returns `true` if the value is a valid phone number, `false` otherwise.
 */
export const isValidPhone = (value: unknown): boolean => {
  if (!isString(value)) {
    return false;
  }

  // Remove all non-digit characters except the leading +
  const cleaned = value
    .replace(/^(\+)/, "PLUS")
    .replace(/\D/g, "")
    .replace(/^PLUS/, "+");

  // Check if empty after cleaning
  if (cleaned.length === 0) {
    return false;
  }

  // Extract just the digits for length validation
  const digitsOnly = cleaned.replace(/^\+/, "");

  // Minimum 7 digits (for local numbers) and maximum 15 (ITU-T E.164 standard)
  if (digitsOnly.length < 7 || digitsOnly.length > 15) {
    return false;
  }

  // Test against the original value (with formatting)
  // Remove excessive spaces first
  const normalizedValue = value.trim().replace(/\s+/g, " ");

  // Check for obviously invalid patterns
  // No consecutive opening or closing parentheses
  if (/\(\(|\)\)/.test(normalizedValue)) {
    return false;
  }

  // No consecutive dashes or dots
  if (/--|\.\./g.test(normalizedValue)) {
    return false;
  }

  // No empty parentheses
  if (/\(\)/.test(normalizedValue)) {
    return false;
  }

  // Ensure parentheses are balanced
  const openParens = (normalizedValue.match(/\(/g) || []).length;
  const closeParens = (normalizedValue.match(/\)/g) || []).length;
  if (openParens !== closeParens) {
    return false;
  }

  // More permissive pattern that allows various formatting
  // This checks:
  // - Optional + at the start
  // - Numbers mixed with common separators (spaces, dashes, dots, parentheses)
  // - Must start and end with a digit or parenthesis (if at start)
  // - Allows spaces around separators
  const pattern = /^[+]?[(]?[0-9]([0-9\s\-().]*[0-9)]?)?$/;

  return pattern.test(normalizedValue);
};
